import type { UseQueryResult } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ExposureChart } from "@/components/exposure-chart";
import type { IncidentGasHistory } from "@/api/queries/incidents";

/**
 * Body of the incident's Gas readings tab. The history loads in pages, and one
 * failed page fails the whole query. A failed load must not fall through to the
 * chart's "No gas readings recorded" empty state: on an after-action review that
 * would be a false statement about the exposure record (RES-460).
 */
export function GasReadingsPanel({
  gas,
  tz,
}: {
  gas: Pick<
    UseQueryResult<IncidentGasHistory>,
    "data" | "isLoading" | "isError" | "refetch"
  >;
  tz?: string;
}) {
  if (gas.isLoading) return <Skeleton className="h-72" />;
  if (gas.isError) {
    return (
      <div role="alert" className="space-y-3">
        <div className="space-y-1">
          <p className="text-sm font-medium text-destructive">
            Couldn&apos;t load gas readings.
          </p>
          <p className="text-sm text-muted-foreground">
            Readings may exist for this incident, but they didn&apos;t load.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void gas.refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  return (
    <ExposureChart
      readings={gas.data?.readings ?? []}
      tz={tz}
      truncatedRows={gas.data?.truncated ? gas.data.rowCount : undefined}
    />
  );
}
